/**
 * @fileoverview Card displaying connected storefronts (Shopify, WooCommerce).
 * @module features/settings/components/StoresCard
 */

import { ReactElement, useState } from 'react'
import { Store as StoreIcon, Plus, ExternalLink, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Skeleton,
} from '@/shared/components/ui'
import { cn } from '@/shared/utils/cn'
import { useStoresQuery } from '../api/storeService'
import { useIntegrationsStatus } from '../hooks/useIntegrationsStatus'
import { ShopifyOAuthConnect } from './ShopifyOAuthConnect'
import { StoreDetailModal } from './StoreDetailModal'
import type { Store } from '../schemas/storeSchema'
import type { IntegrationStatus } from '../schemas/integrationStatusSchema'

/** Platform icon paths */
const PLATFORM_ICONS: Record<string, string> = {
  SHOPIFY: '/shopify.webp',
  WOOCOMMERCE: '/woocommerce.png',
}

/** Platform display names */
const PLATFORM_NAMES: Record<string, string> = {
  SHOPIFY: 'Shopify',
  WOOCOMMERCE: 'WooCommerce',
}

interface StoreEntryProps {
  store: Store
  integration: IntegrationStatus | undefined
  onClick: () => void
}

/**
 * Single store entry in the list.
 */
function StoreEntry({ store, integration, onClick }: StoreEntryProps): ReactElement {
  const { t } = useTranslation('features-settings')

  const isActive = integration?.status === 'ACTIVE'
  const platformName = store.platformType ? PLATFORM_NAMES[store.platformType] : 'Store'
  const platformIcon = store.platformType ? PLATFORM_ICONS[store.platformType] : null

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-between gap-3 w-full rounded-lg border border-border bg-card p-4',
        'cursor-pointer transition-[filter] hover:brightness-105 text-left'
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        {/* Platform icon */}
        <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
          {platformIcon ? (
            <img src={platformIcon} alt={platformName} className="size-5" />
          ) : (
            <StoreIcon className="size-5 text-muted-foreground" />
          )}
        </div>

        {/* Store info */}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground truncate">{store.name}</span>
            {isActive && (
              <Badge
                variant="default"
                className="bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30"
              >
                {t('cards.integrations.status.active')}
              </Badge>
            )}
          </div>
          {store.platformDomain && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <ExternalLink className="size-3" />
              <span className="font-mono truncate">{store.platformDomain}</span>
            </div>
          )}
        </div>
      </div>

      {/* Arrow indicator */}
      <ChevronRight className="size-4 text-muted-foreground shrink-0" />
    </button>
  )
}

/**
 * StoresCard
 *
 * Displays connected storefronts (Shopify, WooCommerce, etc.).
 * Click on a store to open the detail modal for configuration.
 *
 * @component
 */
export function StoresCard(): ReactElement {
  const { t } = useTranslation('features-settings')
  const storesQuery = useStoresQuery()
  const integrationsQuery = useIntegrationsStatus()

  const [selectedStore, setSelectedStore] = useState<Store | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const stores = storesQuery.data ?? []
  const integrations = integrationsQuery.data ?? []

  // Find integration status for a store
  const findIntegration = (store: Store): IntegrationStatus | undefined => {
    if (store.platformType !== 'SHOPIFY') return undefined
    return integrations.find(
      (i) =>
        i.provider === 'SHOPIFY' &&
        (i.connectionId === store.integrationId || i.accountId === store.platformDomain)
    )
  }

  const handleStoreClick = (store: Store): void => {
    setSelectedStore(store)
    setIsModalOpen(true)
  }

  const handleModalClose = (): void => {
    setIsModalOpen(false)
    // Delay clearing selection to allow exit animation
    setTimeout(() => setSelectedStore(null), 200)
  }

  const isLoading = storesQuery.isLoading
  const isError = storesQuery.isError

  return (
    <>
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <StoreIcon className="size-5" />
              {t('cards.stores.title', { defaultValue: 'Stores' })}
            </CardTitle>
            <ShopifyOAuthConnect
              buttonLabel={t('cards.stores.connectStore', { defaultValue: 'Connect Store' })}
              buttonIcon={<Plus className="size-3.5" />}
              connectionMode="ATTRIBUTION_ONLY"
              variant="button"
            />
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              <div className="flex items-center gap-4 p-4 rounded-lg border border-border">
                <Skeleton className="size-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            </div>
          ) : isError ? (
            <div className="text-center py-8 text-destructive">
              {t('cards.integrations.loadError')}
            </div>
          ) : stores.length === 0 ? (
            <div className="text-center py-8">
              <StoreIcon className="size-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-4">
                {t('cards.stores.noStores', { defaultValue: 'No stores connected' })}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                {t('cards.stores.connectHint', {
                  defaultValue: 'Connect your storefront to enable ad spend attribution',
                })}
              </p>
              <ShopifyOAuthConnect
                buttonLabel={t('cards.stores.connectShopify', { defaultValue: 'Connect Shopify' })}
                buttonIcon={<Plus className="size-4" />}
                connectionMode="ATTRIBUTION_ONLY"
              />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Store list */}
              {stores.map((store) => (
                <StoreEntry
                  key={store.id}
                  store={store}
                  integration={findIntegration(store)}
                  onClick={() => handleStoreClick(store)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Store detail modal */}
      {selectedStore && (
        <StoreDetailModal
          store={selectedStore}
          open={isModalOpen}
          onOpenChange={(open) => {
            if (!open) handleModalClose()
          }}
        />
      )}
    </>
  )
}

export default StoresCard
